/**
 * columnDetection.ts - Column Detection Utilities
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
/**
 * Detect columns from data array
 * @param {Array} data - Array of objects
 * @returns {Array} Column definitions for AG Grid
 */
export function detectColumns(data) {
  if (!data || data.length === 0) {
    return [];
  }
  
  const firstRow = data[0];
  const columns = [];
  
  for (const key in firstRow) {
    if (!firstRow.hasOwnProperty(key)) continue;
    
    const value = firstRow[key];
    const type = detectColumnType(value, key, data);
    
    columns.push({
      field: key,
      headerName: formatHeaderName(key),
      type: type,
      filter: getFilterType(type),
      cellDataType: type,
      sortable: true,
      resizable: true,
      editable: !isSystemField(key),
      minWidth: getMinWidth(type),
      valueFormatter: getValueFormatter(type),
      cellRenderer: getCellRenderer(type, key),
    });
  }
  
  return columns;
}

/**
 * Detect column type from value
 * @param {*} value - Sample value
 * @param {string} key - Column key
 * @param {Array} data - All data (for better detection)
 * @returns {string} Column type
 */
function detectColumnType(value, key, data) {
  // Check multiple rows for better detection
  const samples = data.slice(0, 10).map(row => row[key]);
  
  // Number
  if (samples.every(v => typeof v === 'number' || v === null || v === undefined)) {
    return 'number';
  }
  
  // Boolean
  if (samples.every(v => typeof v === 'boolean' || v === null || v === undefined)) {
    return 'boolean';
  }
  
  // Date
  if (samples.some(v => v instanceof Date)) {
    return 'date';
  }
  
  // Date string
  if (samples.some(v => typeof v === 'string' && isValidDateString(v))) {
    return 'date';
  }
  
  // Array
  if (Array.isArray(value)) {
    return 'array';
  }
  
  // Object
  if (typeof value === 'object' && value !== null) {
    return 'object';
  }
  
  // Default to text
  return 'text';
}

/**
 * Check if string is a valid date
 * @param {string} str - String to check
 * @returns {boolean}
 */
function isValidDateString(str) {
  if (typeof str !== 'string') return false;
  
  // ISO 8601 format
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const date = new Date(str);
    return !isNaN(date.getTime());
  }
  
  return false;
}

/**
 * Format header name from field key
 * @param {string} key - Field key
 * @returns {string} Formatted header name
 */
function formatHeaderName(key) {
  // Handle camelCase
  let formatted = key.replace(/([A-Z])/g, ' $1');
  
  // Handle snake_case
  formatted = formatted.replace(/_/g, ' ');
  
  // Handle kebab-case
  formatted = formatted.replace(/-/g, ' ');
  
  // Capitalize first letter of each word
  formatted = formatted.replace(/\b\w/g, l => l.toUpperCase());
  
  // Trim
  formatted = formatted.trim();
  
  return formatted;
}

/**
 * Get filter type for column type
 * @param {string} type - Column type
 * @returns {string|boolean} Filter type
 */
function getFilterType(type) {
  switch (type) {
    case 'number':
      return 'agNumberColumnFilter';
    case 'date':
      return 'agDateColumnFilter';
    case 'boolean':
      // agSetColumnFilter requires Enterprise, use text filter instead
      return 'agTextColumnFilter';
    case 'text':
    default:
      return 'agTextColumnFilter';
  }
}

/**
 * Check if field is a system field (shouldn't be editable)
 * @param {string} key - Field key
 * @returns {boolean}
 */
function isSystemField(key) {
  const systemFields = ['id', '_id', 'createdAt', 'updatedAt', 'created', 'modified'];
  return systemFields.includes(key);
}

/**
 * Get minimum width for column type
 * @param {string} type - Column type
 * @returns {number} Minimum width in pixels
 */
function getMinWidth(type) {
  switch (type) {
    case 'boolean':
      return 80;
    case 'number':
      return 100;
    case 'date':
      return 150;
    case 'array':
    case 'object':
      return 200;
    case 'text':
    default:
      return 120;
  }
}

/**
 * Get value formatter for column type
 * @param {string} type - Column type
 * @returns {Function|undefined} Value formatter
 */
function getValueFormatter(type) {
  switch (type) {
    case 'date':
      return (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString();
      };
    case 'array':
      return (params) => {
        if (!params.value) return '';
        return Array.isArray(params.value) ? params.value.join(', ') : '';
      };
    case 'object':
      return (params) => {
        if (!params.value) return '';
        return JSON.stringify(params.value);
      };
    default:
      return undefined;
  }
}

/**
 * Get cell renderer for column type
 * @param {string} type - Column type
 * @param {string} key - Field key
 * @returns {Function|undefined} Cell renderer
 */
function getCellRenderer(type, key) {
  // Status badge for status fields
  if (key.toLowerCase().includes('status')) {
    return (params) => {
      if (!params.value) return '';
      const status = String(params.value).toLowerCase();
      return `<span class="status-badge status-${status}">${params.value}</span>`;
    };
  }
  
  // Boolean checkbox
  if (type === 'boolean') {
    return (params) => {
      return params.value ? '✓' : '✗';
    };
  }
  
  return undefined;
}

/**
 * Detect if data has hierarchical structure
 * @param {Array} data - Data array
 * @returns {boolean}
 */
export function hasHierarchicalStructure(data) {
  if (!data || data.length === 0) return false;
  
  // Check if any item has children or path
  return data.some(item => 
    (item.children && Array.isArray(item.children)) ||
    (item.path && Array.isArray(item.path)) ||
    item.parentId !== undefined
  );
}

/**
 * Get data path for tree data
 * @param {Object} data - Data item
 * @returns {Array} Path array
 */
export function getDataPath(data) {
  if (data.path && Array.isArray(data.path)) {
    return data.path;
  }
  
  if (data.id) {
    return [data.id];
  }
  
  return [];
}
